import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Trash2, Edit2, Plus, LogOut, Upload, X } from 'lucide-react';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    external_link: '',
    date: new Date().toISOString().split('T')[0]
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Get auth header
  const getAuthHeader = () => {
    const credentials = btoa(`admin:${password}`);
    return { 'Authorization': `Basic ${credentials}` };
  };

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        fetchMediaItems();
      } else {
        setLoginError('Invalid password. Please try again.');
      }
    } catch (error) {
      setLoginError('Connection error. Please try again.');
    }
  };

  // Fetch media items
  const fetchMediaItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/media`);
      if (response.ok) {
        const data = await response.json();
        setMediaItems(data);
      }
    } catch (error) {
      console.error('Error fetching media items:', error);
    }
    setLoading(false);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formDataUpload
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image_url: data.image_url }));
      } else {
        alert('Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image');
    }
    setUploading(false);
  };

  // Handle form submit (create/update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const url = editingItem 
      ? `${API_URL}/api/admin/media/${editingItem.id}`
      : `${API_URL}/api/admin/media`;
    
    const method = editingItem ? 'PUT' : 'POST';
    
    try {
      const response = await fetch(url, {
        method,
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        fetchMediaItems();
        resetForm();
      } else {
        alert('Failed to save media item');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Error saving media item');
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/media/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        fetchMediaItems();
      } else {
        alert('Failed to delete item');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error deleting item');
    }
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      image_url: item.image_url,
      external_link: item.external_link,
      date: item.date
    });
    setShowForm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      external_link: '',
      date: new Date().toISOString().split('T')[0]
    });
    setEditingItem(null);
    setShowForm(false);
  };

  // Logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setMediaItems([]);
  };

  // Scroll to top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="admin-login-page">
        <div className="admin-login-container">
          <div className="admin-login-card">
            <h1 className="heading-2">Admin Login</h1>
            <p className="body-medium">Enter your password to manage media content</p>
            
            <form onSubmit={handleLogin} className="admin-login-form">
              <div className="form-group">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                />
              </div>
              
              {loginError && <p className="error-text">{loginError}</p>}
              
              <Button type="submit" className="btn-primary w-full">
                Login
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <h1 className="heading-2">Media CMS</h1>
            <div className="admin-header-actions">
              <Button 
                onClick={() => { resetForm(); setShowForm(true); }} 
                className="btn-primary"
              >
                <Plus size={18} /> Add New Item
              </Button>
              <Button onClick={handleLogout} variant="outline" className="btn-logout">
                <LogOut size={18} /> Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-content">
        <div className="container">
          {/* Media Items List */}
          <div className="admin-media-list">
            {loading ? (
              <p className="loading-text">Loading media items...</p>
            ) : mediaItems.length === 0 ? (
              <div className="empty-state">
                <p className="body-large">No media items yet</p>
                <p className="body-medium">Click "Add New Item" to create your first media entry.</p>
              </div>
            ) : (
              <div className="media-items-grid">
                {mediaItems.map((item) => (
                  <Card key={item.id} className="admin-media-card">
                    <div className="admin-media-image">
                      <img 
                        src={item.image_url.startsWith('/api') ? `${API_URL}${item.image_url}` : item.image_url} 
                        alt={item.title} 
                      />
                    </div>
                    <div className="admin-media-content">
                      <h3 className="heading-6">{item.title}</h3>
                      <p className="body-small">{item.description}</p>
                      <p className="caption">{new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                    <div className="admin-media-actions">
                      <button onClick={() => handleEdit(item)} className="edit-btn">
                        <Edit2 size={18} />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="delete-btn">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Form Modal - Outside content for proper overlay */}
      {showForm && (
        <div 
          className="admin-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div 
            className="admin-modal"
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            <div className="admin-modal-header">
              <h2 className="heading-4">
                {editingItem ? 'Edit Media Item' : 'Add New Media Item'}
              </h2>
              <button onClick={resetForm} className="modal-close-btn">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Enter title"
                  required
                />
              </div>
              
              <div className="form-group">
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Enter a short description"
                  required
                />
              </div>
              
              <div className="form-group">
                <Label>Image *</Label>
                <div className="image-upload-area">
                  {formData.image_url ? (
                    <div className="image-preview">
                      <img 
                        src={formData.image_url.startsWith('/api') ? `${API_URL}${formData.image_url}` : formData.image_url} 
                        alt="Preview" 
                      />
                      <button 
                        type="button" 
                        onClick={() => setFormData({...formData, image_url: ''})}
                        className="remove-image-btn"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-options">
                      <label className="upload-label">
                        <Upload size={32} />
                        <span>{uploading ? 'Uploading...' : 'Click to upload image'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploading}
                          hidden
                        />
                      </label>
                      <div className="upload-divider">
                        <span>OR</span>
                      </div>
                      <Input
                        type="url"
                        value={formData.image_url}
                        onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                        placeholder="Paste external image URL"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <Label htmlFor="external_link">External Link *</Label>
                <Input
                  id="external_link"
                  type="url"
                  value={formData.external_link}
                  onChange={(e) => setFormData({...formData, external_link: e.target.value})}
                  placeholder="https://example.com/article"
                  required
                />
              </div>
              
              <div className="form-group">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-actions">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary">
                  {editingItem ? 'Update' : 'Create'} Item
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
